import { err } from 'neverthrow';
import { Assert } from '../../../utils';
import { combineRanges, TokenMeta } from '../../lex';
import { sym } from '../../lex/tokens';
import { SpecialForm } from '../types';
import { Call } from '../../eraql-ast/ast';
import { compileAst } from '../../eraql-ast/compile';
import { INFIX_OPERATOR_TO_FC } from './infix-mappings';
import _ from 'lodash';
import { AST } from '../../../ast';

const pipe: SpecialForm = ([lhs, call], ctx) => {
  Assert.assert(
    lhs !== undefined && call !== undefined,
    'Artiy already checked'
  );

  const [_lhsAst, { range: lhsRange }] = lhs;
  const [callAst, { range: callRange }] = call;

  switch (callAst.t) {
    case 'call': {
      const newCall: Call = {
        ...callAst,
        args: [lhs, ...callAst.args],
        display: 'pipe',
      };
      return compileAst(
        [
          newCall,
          {
            range: combineRanges(lhsRange, callRange),
            comments: { leading: [], trailing: null }, // Intentionally drop comments as they are not needed for compilations
          },
        ],
        ctx
      );
    }
    case 'symbol': {
      // Case: `-5 |> abs` should eval to `5`
      const newCall: Call = {
        t: 'call',
        op: call,
        args: [lhs],
        display: 'pipe',
      };
      return compileAst(
        [
          newCall,
          {
            range: combineRanges(lhsRange, callRange),
            comments: { leading: [], trailing: null }, // Intentionally drop comments as they are not needed for compilations
          },
        ],
        ctx
      );
    }
    default:
      return err([[{ t: 'cant-pipe-into' }, { range: callRange }]]);
  }
};

// TODO: Incorrect code here. Op's token meta should be passed here.
const callInfix =
  (op: string): SpecialForm =>
  ([lhs, rhs], ctx) => {
    Assert.assert(
      lhs !== undefined && rhs !== undefined,
      'Artiy already checked'
    );

    const callRange = combineRanges(lhs[1].range, rhs[1].range);
    const callMeta: TokenMeta = {
      range: callRange,
      comments: { leading: [], trailing: null }, // Intentionally drop comments as they are not needed for compilation
    };

    const newCall: Call = {
      t: 'call',
      op: [sym(op), callMeta], // TODO we lost Op's token meta, so we are just using the call's meta instead.
      args: [lhs, rhs],
      display: 'infix',
    };
    return compileAst([newCall, callMeta], ctx);
  };

/**
 * Semicolon that appears in a `e1;e2` statement (not a let s = e1; e2) expression
 */
const semicolon: SpecialForm = ([lhs, rhs], ctx) => {
  Assert.assert(lhs !== undefined && rhs !== undefined);
  const left = compileAst(lhs, ctx);

  if (left.isErr()) {
    return err(left.error);
  }

  return compileAst(rhs, ctx);
};

export const INFIX_SPECIAL_FORMS: Record<
  string,
  [artiy: number | number[] | 'any', fn: SpecialForm]
> = {
  ';': [2, semicolon], // Semicolon that appears in a `e1;e2` statement (not a let s = e1; e2) expression
  ..._.mapValues(
    INFIX_OPERATOR_TO_FC,
    (op) => [2, callInfix(op as AST.FunctionIdentifier)] as [2, SpecialForm]
  ),
  '|>': [2, pipe],
};
